import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { getFarm } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function FarmDetail() {
    const { farmId } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null);
        getFarm(farmId)
            .then(setData)
            .catch(() => setNotFound(true));
    }, [farmId]);

    if (notFound) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-40 pb-24 text-center">
                <h1 className="font-serif-display text-4xl">Farm not found.</h1>
                <Link to="/farms" className="btn-luxe btn-green mt-8 inline-flex">All Farms</Link>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-32 pb-24 px-5">
                <div className="max-w-[1280px] mx-auto space-y-6">
                    <Skeleton className="h-96 rounded-xl bg-black/[0.06]" />
                    <Skeleton className="h-10 w-1/3 bg-black/[0.06]" />
                    <Skeleton className="h-32 bg-black/[0.06]" />
                </div>
            </main>
        );
    }

    const { farm, products } = data;

    return (
        <main className="bg-[#F6F1E7] min-h-screen pb-24" data-testid="farm-detail-page">
            {/* Hero */}
            <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
                <img src={farm.images[0]} alt={farm.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/85 via-[#0B0B0C]/25 to-[#0B0B0C]/40" />
                <div className="relative z-10 h-full flex flex-col justify-end max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 pb-14">
                    <p className="eyebrow text-[#C9B37A]">{farm.location_name}</p>
                    <h1 className="font-serif-display text-5xl sm:text-6xl font-medium text-[#F6F1E7] mt-2" data-testid="farm-detail-name">
                        {farm.name}
                    </h1>
                </div>
            </section>

            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                {/* Stats bar */}
                <Reveal>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-black/10 border border-black/10 rounded-xl overflow-hidden -mt-0 relative z-20 translate-y-[-32px] shadow-xl">
                        {[
                            ["FARMER", farm.farmer_name],
                            ["BREED", farm.breed],
                            ["ESTABLISHED", farm.established],
                            ["CATTLE RAISED", farm.cattle_count],
                            ["PARTNER SINCE", farm.partnership_since],
                        ].map(([label, value]) => (
                            <div key={label} className="bg-[#F6F1E7] p-5 text-center">
                                <p className="font-mono-meta text-[9px] tracking-[0.2em] text-black/40">{label}</p>
                                <p className="font-serif-display text-lg font-medium mt-1.5">{value}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* Story */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10">
                    <Reveal className="lg:col-span-7">
                        <p className="eyebrow text-[#8C6A3D] mb-4">The Farm</p>
                        <p className="font-serif-display text-2xl sm:text-3xl italic text-black/80 leading-snug">
                            “{farm.quote}”
                        </p>
                        <p className="font-mono-meta text-[10px] tracking-[0.2em] text-black/45 mt-4">
                            — {farm.farmer_name.toUpperCase()}
                        </p>
                        <div className="mt-8 space-y-5 text-base text-black/65 leading-relaxed">
                            <p>{farm.story}</p>
                        </div>
                        <p className="eyebrow text-[#8C6A3D] mt-10 mb-4">Family History</p>
                        <p className="text-base text-black/65 leading-relaxed">{farm.family_history}</p>
                        <p className="mt-8 text-sm text-black/50">
                            Farming ethos: <span className="text-[#0F2A1F] font-semibold">{farm.ethos}</span>
                        </p>
                    </Reveal>
                    <Reveal className="lg:col-span-5" delay={0.12}>
                        <div className="rounded-xl overflow-hidden">
                            <img src={farm.portrait} alt={farm.farmer_name} loading="lazy" className="w-full aspect-[3/4] object-cover" />
                        </div>
                        <p className="font-mono-meta text-[10px] tracking-[0.2em] text-black/45 text-center mt-4">
                            {farm.farmer_name.toUpperCase()} · {farm.region.toUpperCase()}
                        </p>
                        {farm.images[1] && (
                            <div className="rounded-xl overflow-hidden mt-5">
                                <img src={farm.images[1]} alt={`${farm.name} landscape`} loading="lazy" className="w-full aspect-[16/10] object-cover" />
                            </div>
                        )}
                    </Reveal>
                </div>

                {/* Products from this farm */}
                {products.length > 0 && (
                    <div className="mt-24">
                        <Reveal className="text-center mb-10">
                            <p className="eyebrow text-[#8C6A3D] mb-3">From this farm</p>
                            <h2 className="font-serif-display text-3xl sm:text-4xl font-medium">
                                Taste {farm.name.split(" ")[0]}'s work
                            </h2>
                        </Reveal>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            {products.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
